import { memo, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import WarningIcon from '@mui/icons-material/WarningAmberRounded';
import { Folder } from 'react-feather';
import { type Cell } from 'react-table';
import { Brand as BrandIcon } from '@/components/icons/Brand';
import { FileIcon, TranscriptFileIcon } from '@trinity/chat-components/components/icons/File/index.js';
import ProjectIcon from '@/components/icons/ProjectIcon';
import { MaybeLink } from '@trinity/chat-components/components/presentation/index.js';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip.js';
import { TranscriptionRevisionStatus, WorkspaceObjectType } from '@/enums';
import { useIsInternalUser } from '@/store/selectors';
import { FileTranscriptStatus } from '@/types';
import type { IWorkspaceBrandItem, IWorkspaceFileItem, IWorkspaceFolderItem, IWorkspaceProjectItem, WorkspaceObjectItem } from '@/types/workspace.table';
import { cx, getLocationFor } from '@trinity/chat-components';
import styles from './style/Files.Table.Cells.module.css';

export function NameCell(props: Cell<WorkspaceObjectItem>) {
  switch (props.row.original.object.typeId) {
    case WorkspaceObjectType.Brand:
      return <BrandNameCell {...(props as Cell<IWorkspaceBrandItem>)} />;

    case WorkspaceObjectType.File:
      return <FileNameCell {...(props as Cell<IWorkspaceFileItem>)} />;

    case WorkspaceObjectType.Folder:
      return <FolderNameCell {...(props as Cell<IWorkspaceFolderItem>)} />;

    case WorkspaceObjectType.ProjectParent:
      return <ProjectNameCell {...(props as Cell<IWorkspaceProjectItem>)} />;

    case WorkspaceObjectType.CallTranscript:
      // return <TranscriptNameCell {...(props as Cell<IWorkspaceTranscriptItem>)} />;
      return null;

    case WorkspaceObjectType.Workspace:
      return null;

    default:
      throw new UnreachableCaseError(props.row.original.object.typeId);
  }
}

function BrandNameCell({ row: { original: data } }: Cell<IWorkspaceBrandItem>) {
  const to = getLocationFor.workspaces.brand({
    brandId: data.brand.id,
  });

  return (
    <div className={styles.name}>
      <BrandIcon
        className={styles.icon} />
      <StyledLink to={to}>
        <div className={styles.trunc}>{data.object.name}</div>
      </StyledLink>
    </div>
  );
}

// function TranscriptNameCell({ row: { id, original: data } }: Cell<IWorkspaceTranscriptItem>) {
//   const firstTranscriptRowId = useContext(FirstTranscriptRowIdContext);
//   const [containerRef, setContainerRef] = useState<HTMLDivElement>(null);

//   const to = useTranscriptLink(data);
//   const isInternalUser = useIsInternalUser();

//   const loc = isInternalUser || data.transcript.enhancement?.statusId === TranscriptionRevisionStatus.Completed
//     ? to
//     : null;

//   return (
//     <div
//       className={styles.transcript}
//       ref={setContainerRef}>
//       <Tooltip title={data.object.name}>
//         <div className={styles.name}>
//           <TranscriptFileIcon
//             className={styles.icon} />
//           <MaybeLink className={cx({ [styles.link]: !!loc })} to={loc}>
//             <div className={styles.trunc}>{data.object.name}</div>
//           </MaybeLink>
//         </div>
//       </Tooltip>
//       {/* <VisibilityStatus statusId={data.transcript.enhancement?.statusId} /> */}
//       {(id === firstTranscriptRowId && containerRef) && <DisclaimerPopper containerRef={containerRef} />}
//     </div>
//   );
// }

function ProjectNameCell({ row: { original: data } }: Cell<IWorkspaceProjectItem>) {
  const to = getLocationFor.project.root({ slug: data.project.id.toString() });
  return (
    <div className={styles.name}>
      <ProjectIcon
        className={styles.icon}
        projectTypeId={data.project.projectTypeId} />
      <StyledLink to={to}>
        <div className={styles.trunc}>{data.object.name}</div>
      </StyledLink>
    </div>
  );
}

function FolderNameCell({ row: { original: data } }: Cell<IWorkspaceFolderItem>) {

  const to = getLocationFor.workspaces.folder({
    folderId: data.folder.id,
  });

  return (
    <div className={styles.name}>
      <Folder
        className={styles.icon}
        color="#737373"
        size={26} />
      <StyledLink to={to}>
        <div className={styles.trunc}>{data.object.name}</div>
      </StyledLink>
    </div>
  );
}

function FileNameCell(props: Cell<IWorkspaceFileItem>) {
  const data = props.row.original;

  const to = getLocationFor.workspaces.file({ fileId: data.file.id });

  const hasTranscript = useMemo(() => {
    return data.transcript?.status === FileTranscriptStatus.Complete;
  }, [data.transcript?.status]);

  if (hasTranscript) {
    return <FileTranscriptCell {...props} />;
  }

  return (
    <div className={styles.name}>
      <FileIcon
        className={styles.icon}
        extension={data.file.extension} />
      <div className={styles.fileName}>
        <StyledLink to={to}>
          <div className={styles.trunc}>{data.object.name}</div>
        </StyledLink>
        {data.object.isExpired && <ExpiredStatus />}
      </div>
    </div>
  );
}

const ExpiredStatus = memo(() => {
  return (
    <Tooltip title="Expired">
      <div className={styles.expired}>
        <WarningIcon />
      </div>
    </Tooltip>
  );
});

function FileTranscriptCell({ row: { id, original: data } }: Cell<IWorkspaceFileItem>) {
  //const firstTranscriptRowId = useContext(FirstTranscriptRowIdContext);
  //const [containerRef, setContainerRef] = useState<HTMLDivElement>(null);

  const isInternalUser = useIsInternalUser();

  const transcriptLocation = useMemo(() => {
    const loc = getLocationFor.workspaces.transcript({
      transcriptId: data.transcript.id,
    });

    if (isInternalUser) {
      return loc;
    }

    const enhancementStatusId = data.transcript.enhancement?.statusId;

    if (!enhancementStatusId || enhancementStatusId === TranscriptionRevisionStatus.Completed) {
      return loc;
    }

    return null;
  }, [data.transcript.enhancement?.statusId, data.transcript.id, isInternalUser]);

  return (
    <div
      className={styles.transcript}>
      <div className={styles.name}>
        <TranscriptFileIcon
          className={styles.icon} />
        <div className={styles.fileName}>
          <StyledMaybeLink to={transcriptLocation}>
            <Tooltip title={data.object.name}>
              <div className={styles.trunc}>{data.object.name}</div>
            </Tooltip>
          </StyledMaybeLink>
        </div>
      </div>
    </div>
  );
}

const StyledLink = styled(Link)(({ theme }) => ({
  overflow: 'hidden',
  '&:hover': {
    color: theme.palette.hyperlink,
    textDecoration: 'underline',
  },
}));
const StyledMaybeLink = styled(MaybeLink)(({ theme, to }) => ({
  overflow: 'hidden',
  ...(to && {
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.hyperlink,
      textDecoration: 'underline',
    },
  }),
}));